import { useEffect, useState } from "react";

/**
 * Premium Factory World loading animation.
 * Isometric factory icon with orbiting assembly particles,
 * blueprint grid background, and animated progress conveyor.
 */
export default function FactoryLoadingAnimation() {
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState("Initializing");

  useEffect(() => {
    const stages = [
      { at: 0, text: "Initializing" },
      { at: 20, text: "Loading projects" },
      { at: 45, text: "Syncing agents" },
      { at: 70, text: "Building world" },
      { at: 90, text: "Almost ready" },
    ];

    const interval = setInterval(() => {
      setProgress((p) => {
        const next = Math.min(p + Math.random() * 3 + 0.5, 95);
        const stage = [...stages].reverse().find((s) => s.at <= next);
        if (stage) setStatusText(stage.text);
        return next;
      });
    }, 120);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="factory-loader">
      {/* Blueprint grid background */}
      <div className="factory-loader__grid" />

      {/* Ambient glow */}
      <div className="factory-loader__glow" />

      {/* Central assembly */}
      <div className="factory-loader__center">
        {/* Orbiting particles */}
        <div className="factory-loader__orbit">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="factory-loader__particle"
              style={{
                ["--i" as string]: i,
                ["--total" as string]: 8,
              }}
            />
          ))}
        </div>

        {/* Factory icon SVG with draw animation */}
        <svg
          className="factory-loader__icon"
          viewBox="0 0 64 64"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Factory body - draws in */}
          <path
            className="factory-loader__stroke factory-loader__stroke--1"
            d="M8 56V16a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8l10-7.5v7.5l10-7.5v7.5l10-7.5V56"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* Roof line */}
          <path
            className="factory-loader__stroke factory-loader__stroke--2"
            d="M4 56h56"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          {/* Chimney */}
          <path
            className="factory-loader__stroke factory-loader__stroke--3"
            d="M12 14V6M16 14V8"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          {/* Windows */}
          <rect
            className="factory-loader__fill factory-loader__fill--1"
            x="11" y="36" width="7" height="7" rx="1"
          />
          <rect
            className="factory-loader__fill factory-loader__fill--2"
            x="23" y="36" width="7" height="7" rx="1"
          />
          <rect
            className="factory-loader__fill factory-loader__fill--3"
            x="35" y="36" width="7" height="7" rx="1"
          />
          <rect
            className="factory-loader__fill factory-loader__fill--4"
            x="47" y="36" width="7" height="7" rx="1"
          />
          {/* Door */}
          <rect
            className="factory-loader__fill factory-loader__fill--5"
            x="26" y="46" width="12" height="10" rx="1.5"
          />
          {/* Smoke particles */}
          <circle className="factory-loader__smoke factory-loader__smoke--1" cx="12" cy="4" r="1.5" />
          <circle className="factory-loader__smoke factory-loader__smoke--2" cx="16" cy="5" r="1.2" />
          <circle className="factory-loader__smoke factory-loader__smoke--3" cx="14" cy="2" r="1" />
        </svg>

        {/* Sparks */}
        <div className="factory-loader__sparks">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="factory-loader__spark"
              style={{ ["--j" as string]: i }}
            />
          ))}
        </div>
      </div>

      {/* Progress conveyor */}
      <div className="factory-loader__conveyor">
        <div className="factory-loader__track">
          <div
            className="factory-loader__progress"
            style={{ transform: `scaleX(${progress / 100})` }}
          />
          {/* Conveyor dots */}
          <div className="factory-loader__dots">
            {Array.from({ length: 20 }).map((_, i) => (
              <div key={i} className="factory-loader__dot" />
            ))}
          </div>
        </div>
      </div>

      {/* Status text */}
      <div className="factory-loader__status">
        <span className="factory-loader__text">{statusText}</span>
        <span className="factory-loader__ellipsis" />
      </div>

      <style>{`
        .factory-loader {
          position: fixed;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: hsl(220 15% 8%);
          overflow: hidden;
          z-index: 9999;
        }

        /* Blueprint grid */
        .factory-loader__grid {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(245, 184, 0, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(245, 184, 0, 0.03) 1px, transparent 1px),
            linear-gradient(rgba(245, 184, 0, 0.015) 1px, transparent 1px),
            linear-gradient(90deg, rgba(245, 184, 0, 0.015) 1px, transparent 1px);
          background-size: 100px 100px, 100px 100px, 20px 20px, 20px 20px;
          animation: gridPan 20s linear infinite;
        }

        @keyframes gridPan {
          to { transform: translate(-100px, -100px); }
        }

        /* Ambient glow behind factory */
        .factory-loader__glow {
          position: absolute;
          width: 400px;
          height: 400px;
          border-radius: 50%;
          background: radial-gradient(
            circle,
            rgba(245, 184, 0, 0.12) 0%,
            rgba(0, 178, 200, 0.06) 40%,
            transparent 70%
          );
          animation: glowPulse 3s ease-in-out infinite;
        }

        @keyframes glowPulse {
          0%, 100% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.15); opacity: 1; }
        }

        /* Center container */
        .factory-loader__center {
          position: relative;
          width: 180px;
          height: 180px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* Orbiting particles */
        .factory-loader__orbit {
          position: absolute;
          inset: 0;
          animation: orbitSpin 12s linear infinite;
        }

        @keyframes orbitSpin {
          to { transform: rotate(360deg); }
        }

        .factory-loader__particle {
          position: absolute;
          width: 4px;
          height: 4px;
          border-radius: 50%;
          top: 50%;
          left: 50%;
          --angle: calc(var(--i) * (360 / var(--total)) * 1deg);
          --radius: 82px;
          transform:
            rotate(var(--angle))
            translateX(var(--radius))
            rotate(calc(-1 * var(--angle)));
          background: #F5B800;
          box-shadow: 0 0 8px 2px rgba(245, 184, 0, 0.5);
          animation: particlePulse 2s ease-in-out infinite;
          animation-delay: calc(var(--i) * 0.25s);
        }

        .factory-loader__particle:nth-child(even) {
          background: #00B2C8;
          box-shadow: 0 0 8px 2px rgba(0, 178, 200, 0.5);
          width: 3px;
          height: 3px;
        }

        @keyframes particlePulse {
          0%, 100% { opacity: 0.4; transform: rotate(var(--angle)) translateX(var(--radius)) rotate(calc(-1 * var(--angle))) scale(1); }
          50% { opacity: 1; transform: rotate(var(--angle)) translateX(var(--radius)) rotate(calc(-1 * var(--angle))) scale(1.8); }
        }

        /* Factory icon */
        .factory-loader__icon {
          width: 100px;
          height: 100px;
          filter: drop-shadow(0 0 20px rgba(245, 184, 0, 0.3));
        }

        /* Stroke draw animation */
        .factory-loader__stroke {
          stroke: #F5B800;
          stroke-dasharray: 300;
          stroke-dashoffset: 300;
          animation: strokeDraw 2s ease-out forwards;
        }

        .factory-loader__stroke--2 {
          animation-delay: 0.6s;
        }

        .factory-loader__stroke--3 {
          stroke-dasharray: 30;
          stroke-dashoffset: 30;
          animation-delay: 1s;
          animation-duration: 0.8s;
        }

        @keyframes strokeDraw {
          to { stroke-dashoffset: 0; }
        }

        /* Window fills fade in */
        .factory-loader__fill {
          fill: rgba(0, 178, 200, 0.6);
          opacity: 0;
          animation: windowLight 0.4s ease-out forwards;
        }

        .factory-loader__fill--1 { animation-delay: 1.4s; }
        .factory-loader__fill--2 { animation-delay: 1.6s; }
        .factory-loader__fill--3 { animation-delay: 1.8s; }
        .factory-loader__fill--4 { animation-delay: 2.0s; }
        .factory-loader__fill--5 {
          fill: rgba(245, 184, 0, 0.25);
          animation-delay: 2.2s;
        }

        @keyframes windowLight {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }

        /* After windows light up, they pulse */
        .factory-loader__fill--1,
        .factory-loader__fill--2,
        .factory-loader__fill--3,
        .factory-loader__fill--4 {
          animation: windowLight 0.4s ease-out forwards,
                     windowPulse 3s ease-in-out 2.5s infinite;
        }

        @keyframes windowPulse {
          0%, 100% { fill: rgba(0, 178, 200, 0.5); }
          50% { fill: rgba(0, 178, 200, 0.9); }
        }

        /* Smoke animation */
        .factory-loader__smoke {
          fill: rgba(255, 255, 255, 0.15);
          opacity: 0;
          animation: smokeRise 2.5s ease-out 1.2s infinite;
        }

        .factory-loader__smoke--2 {
          animation-delay: 1.8s;
        }

        .factory-loader__smoke--3 {
          animation-delay: 2.4s;
        }

        @keyframes smokeRise {
          0% { opacity: 0; transform: translateY(0) scale(1); }
          30% { opacity: 0.4; }
          100% { opacity: 0; transform: translateY(-14px) scale(2.5); }
        }

        /* Sparks */
        .factory-loader__sparks {
          position: absolute;
          inset: 0;
          pointer-events: none;
        }

        .factory-loader__spark {
          position: absolute;
          width: 2px;
          height: 2px;
          background: #F5B800;
          border-radius: 50%;
          top: 50%;
          left: 50%;
          opacity: 0;
          animation: sparkFly 3s ease-out infinite;
          animation-delay: calc(var(--j) * 0.5s);
        }

        @keyframes sparkFly {
          0% {
            opacity: 0;
            transform: translate(0, 0) scale(1);
          }
          10% {
            opacity: 1;
          }
          100% {
            opacity: 0;
            transform:
              translate(
                calc((var(--j) - 3) * 30px),
                calc(-40px - var(--j) * 8px)
              )
              scale(0);
          }
        }

        /* Conveyor progress bar */
        .factory-loader__conveyor {
          margin-top: 48px;
          width: 280px;
        }

        .factory-loader__track {
          position: relative;
          height: 4px;
          background: rgba(255, 255, 255, 0.06);
          border-radius: 2px;
          overflow: hidden;
        }

        .factory-loader__progress {
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, #F5B800, #00B2C8);
          border-radius: 2px;
          transform-origin: left center;
          transition: transform 0.3s cubic-bezier(0.2, 0, 0, 1);
          box-shadow: 0 0 12px rgba(245, 184, 0, 0.4);
        }

        /* Rolling conveyor dots */
        .factory-loader__dots {
          position: absolute;
          inset: 0;
          display: flex;
          gap: 10px;
          align-items: center;
          padding: 0 2px;
          animation: conveyorRoll 2s linear infinite;
        }

        .factory-loader__dot {
          flex-shrink: 0;
          width: 2px;
          height: 2px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.15);
        }

        @keyframes conveyorRoll {
          to { transform: translateX(-12px); }
        }

        /* Status text */
        .factory-loader__status {
          margin-top: 20px;
          display: flex;
          align-items: center;
          gap: 2px;
        }

        .factory-loader__text {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.5);
          letter-spacing: 0.02em;
        }

        .factory-loader__ellipsis::after {
          content: '';
          animation: ellipsis 1.5s steps(4, end) infinite;
        }

        @keyframes ellipsis {
          0% { content: ''; }
          25% { content: '.'; }
          50% { content: '..'; }
          75% { content: '...'; }
        }
      `}</style>
    </div>
  );
}
